import React, { useId, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence, Reorder, useDragControls, useReducedMotion } from 'framer-motion';
import { Plus, Trash2, GripVertical, Check, X, Settings2 } from 'lucide-react';
import { HelpButton } from '@/components/ui/help-button';
import { Switch } from '@/components/ui/switch';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { CustomizedFieldIndicator } from '../../form/CustomizedFieldIndicator';
import { fieldLabelClass, fieldDescriptionClass } from '@/components/config_forms/shared/surcharge/fieldStyles';
import { MULTIPLIER_SUGGESTIONS } from '@/components/config_forms/multipliers/multiplierUtils';
import { cn } from '@/lib/utils';
import { EASE_EXPO_OUT, DURATION_FAST, DURATION_MEDIUM } from '@/lib/motion';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useOverflowAnchorLock } from '@/hooks/useOverflowAnchorLock';
import { toast } from '@/components/ui/sonner';
import type { ExtendedTier } from '@/types/ExtendedTier';
import { MAX_EXTENDED_TIER_RATE, normalizeExtendedTiers, sortExtendedTiers, validateExtendedTierCandidate } from '@/utils/extendedOvertime';

interface ExtendedOvertimeSectionProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  tiers: ExtendedTier[];
  onTiersChange: (tiers: ExtendedTier[]) => void;
  highestOvertimeRate: number;
  isCustomized?: boolean;
  context: 'user' | 'client';
  defaultTiers?: ExtendedTier[];
  /** Markdown content for the help sheet. When provided, a help button appears next to the label. */
  helpContent?: string;
  /** Field name prefix for the help sheet title (e.g. "Short Turn"). */
  helpFieldNamePrefix?: string;
  /** Template variables for the help sheet markdown. */
  helpVariables?: Record<string, unknown>;
  /** Injectable React components for the help sheet (rendered via <INJECT:name/> markers). */
  helpInjectableComponents?: Record<string, React.ReactNode>;
  /** Display name of the highest base ladder tier (shown in the ghost card). */
  highestOvertimeTierName?: string;
  /** Maximum number of extended tiers allowed (derived from the rate ladder size). */
  maxTiers?: number;
  /** Ref to expose the configure action to parent components. */
  configureRef?: React.MutableRefObject<(() => void) | null>;
  /** Render only the tier editor shell (used for help-sheet inline editing). */
  mode?: 'full' | 'editor-only';
  /** Override the default label class (e.g. to match help-sheet ControlRow tokens). */
  labelClassName?: string;
  /** Override the default description class (e.g. to match help-sheet ControlRow body tokens). */
  descriptionClassName?: string;
}

interface EditState {
  mode: 'add' | 'edit';
  name: string;
  rate: number | null;
  editIndex: number | null;
  editSortOrder: number | null;
}

const EMPTY_ADD_STATE: EditState = {
  mode: 'add',
  name: '',
  rate: null,
  editIndex: null,
  editSortOrder: null,
};

// --- Draggable tier card (needs its own useDragControls) ---
const DraggableTierCard: React.FC<{
  tier: ExtendedTier;
  idx: number;
  isEditing: boolean;
  editForm: React.ReactNode;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  constraintsRef: React.RefObject<HTMLElement | null>;
  disabled: boolean;
  dragDisabled: boolean;
  unreachable: boolean;
  totalCount: number;
  isMobile: boolean;
  reducedMotion: boolean;
  shouldAllowMobileTap: () => boolean;
}> = ({
  tier,
  idx,
  isEditing,
  editForm,
  onEdit,
  onDelete,
  onDragStart,
  onDragEnd,
  onMoveUp,
  onMoveDown,
  constraintsRef,
  disabled,
  dragDisabled,
  unreachable,
  totalCount,
  isMobile,
  reducedMotion,
  shouldAllowMobileTap,
}) => {
  const dragControls = useDragControls();

  const enterExitTransition = reducedMotion
    ? { duration: 0 }
    : { duration: DURATION_MEDIUM, ease: EASE_EXPO_OUT };

  if (isEditing) {
    return (
      <Reorder.Item value={tier} dragListener={false} className="list-none overflow-hidden" layout={reducedMotion ? undefined : "position"}
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        transition={reducedMotion ? { duration: 0 } : { duration: DURATION_MEDIUM, ease: EASE_EXPO_OUT, opacity: enterExitTransition, height: enterExitTransition }}>
        {editForm}
      </Reorder.Item>
    );
  }

  return (
    <Reorder.Item
      value={tier}
      dragControls={dragControls}
      dragListener={false}
      dragConstraints={constraintsRef}
      dragElastic={0.05}
      className="list-none overflow-hidden"
      layout={reducedMotion ? undefined : "position"}
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={reducedMotion ? { duration: 0 } : { duration: DURATION_MEDIUM, ease: EASE_EXPO_OUT, opacity: enterExitTransition, height: enterExitTransition }}
      whileDrag={{ scale: 1.02, boxShadow: '0 4px 16px rgba(0,0,0,0.3)', zIndex: 10 }}
      onDragEnd={onDragEnd}
    >
      <div
        data-anim-audit="short-turn-extended-overtime-tier-card"
        data-anim-audit-tier-index={idx}
        className={cn(
          'group grid grid-cols-[auto_56px_1fr_auto_auto] items-center gap-2 px-3 py-2',
          'border border-border/60 bg-surface-1 dark:bg-transparent rounded equipment-card',
          unreachable && 'opacity-45',
          // Mobile: tap card to edit
          !disabled && !unreachable && 'md:cursor-default cursor-pointer active:translate-y-px',
          'transition-[background-color,box-shadow,border-color,opacity] duration-150'
        )}
        {...(isMobile && !disabled && !unreachable ? { role: 'button' as const, tabIndex: 0, 'aria-label': `Edit ${tier.name}` } : {})}
        onClick={() => {
          if (isMobile && !disabled && shouldAllowMobileTap()) onEdit(idx);
        }}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (isMobile && !disabled && !unreachable && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            if (shouldAllowMobileTap()) onEdit(idx);
          }
        }}
      >
        {/* Drag handle — 44px touch target, keyboard reorder via arrow keys */}
        <div
          role="button"
          tabIndex={dragDisabled ? -1 : 0}
          aria-roledescription="sortable"
          aria-label={`Reorder ${tier.name}, position ${idx + 1} of ${totalCount}`}
          className="-ml-3 -my-2 flex items-center justify-center w-11 h-11 touch-none cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground focus-visible:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded transition-colors"
          onPointerDown={(e) => {
            if (!dragDisabled) {
              onDragStart();
              dragControls.start(e);
            }
          }}
          onKeyDown={(e) => {
            if (dragDisabled) return;
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              onMoveUp(idx);
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              onMoveDown(idx);
            }
          }}
        >
          <GripVertical className="h-4 w-4" />
        </div>
        <div className={cn(
          'font-mono text-sm font-bold tabular-nums',
          unreachable ? 'text-muted-foreground/60' : 'text-primary-text'
        )}>
          {tier.rate != null ? `${tier.rate}x` : '\u2014'}
        </div>
        <div className={cn(
          'font-heading text-[13px] uppercase tracking-wide truncate',
          unreachable ? 'text-muted-foreground' : 'text-foreground'
        )}>
          {tier.name}
        </div>
        <span className={cn(
          'inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] sm:text-[12px] font-mono font-semibold leading-none',
          unreachable
            ? 'bg-muted text-muted-foreground'
            : 'bg-primary/15 text-primary-text'
        )}>
          {unreachable ? 'Unreachable' : `Ext ${idx + 1}`}
        </span>
        {/* Action buttons: hidden on mobile, always visible on tablet, hover-reveal on desktop */}
        <div className="hidden md:flex items-center gap-1 overflow-hidden md:max-w-[60px] lg:max-w-0 lg:group-hover:max-w-[60px] transition-[max-width] duration-200 ease-in-out">
          <button
            type="button"
            data-anim-audit="short-turn-extended-overtime-edit-tier"
            className="relative w-7 h-7 shrink-0 flex items-center justify-center rounded text-muted-foreground/60 hover:text-foreground hover:bg-muted/30 transition-colors duration-150 after:content-[''] after:absolute after:-inset-2"
            onClick={(e) => { e.stopPropagation(); if (!disabled) onEdit(idx); }}
            aria-label={`Edit ${tier.name}`}
          >
            <Settings2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            data-anim-audit="short-turn-extended-overtime-delete-tier"
            className="relative w-7 h-7 shrink-0 flex items-center justify-center rounded text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10 transition-colors duration-150 after:content-[''] after:absolute after:-inset-2"
            onClick={(e) => { e.stopPropagation(); onDelete(idx); }}
            aria-label={`Delete ${tier.name}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </Reorder.Item>
  );
};

export const ExtendedOvertimeSection: React.FC<ExtendedOvertimeSectionProps> = ({
  enabled,
  onEnabledChange,
  tiers,
  onTiersChange,
  highestOvertimeRate,
  isCustomized,
  context,
  defaultTiers,
  helpContent,
  helpFieldNamePrefix,
  helpVariables,
  helpInjectableComponents,
  highestOvertimeTierName,
  maxTiers,
  configureRef,
  mode = 'full',
  labelClassName = fieldLabelClass,
  descriptionClassName = fieldDescriptionClass,
}) => {
  const toggleId = useId();
  const shouldReduceMotion = useReducedMotion();
  const isMobileViewport = useMediaQuery('(max-width: 767px)');
  const [editState, setEditState] = useState<EditState | null>(null);
  const [stagedAddState, setStagedAddState] = useState<EditState | null>(null);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const inlineSectionRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const suppressMobileTapUntilRef = useRef(0);
  const isDraggingRef = useRef(false);
  const previousEditorKeyRef = useRef<string | null>(null);
  const addRegionRef = useRef<HTMLDivElement>(null);
  const addRegionLockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Height of the add-button area captured before it collapses (used as min-height floor on save). */
  const addButtonHeightRef = useRef(0);
  useOverflowAnchorLock(inlineSectionRef);

  const collapseDuration = shouldReduceMotion ? 0 : Math.round(DURATION_MEDIUM * 1000);

  const sortedTiers = useMemo(() => {
    return normalizeExtendedTiers(tiers, highestOvertimeRate);
  }, [tiers, highestOvertimeRate]);

  useEffect(() => {
    const rawSorted = sortExtendedTiers(tiers).map(t => ({ name: t.name.trim(), rate: t.rate, sortOrder: t.sortOrder }));
    const normalized = sortedTiers.map(t => ({ name: t.name.trim(), rate: t.rate, sortOrder: t.sortOrder }));
    if (JSON.stringify(rawSorted) !== JSON.stringify(normalized)) {
      onTiersChange(sortedTiers);
    }
  }, [tiers, sortedTiers, onTiersChange]);

  // Local order state for drag — maintains object references so framer-motion can animate
  const [localOrder, setLocalOrder] = useState<ExtendedTier[]>(sortedTiers);
  const sortedTiersRef = useRef<ExtendedTier[]>(sortedTiers);

  // Sync local order from parent tiers for non-drag updates (add/edit/delete/external updates).
  useEffect(() => {
    isDraggingRef.current = false;
    sortedTiersRef.current = sortedTiers;
    setLocalOrder(sortedTiers);
    setEditState((current) => {
      if (!current) return current;
      if (current.mode === 'add') {
        return null;
      }

      const matchedIndex = current.editSortOrder != null
        ? sortedTiers.findIndex((tier) => tier.sortOrder === current.editSortOrder)
        : -1;

      if (matchedIndex < 0) {
        return null;
      }

      const matchedTier = sortedTiers[matchedIndex];
      return {
        ...current,
        name: matchedTier.name,
        rate: matchedTier.rate,
        editIndex: matchedIndex,
        editSortOrder: matchedTier.sortOrder,
      };
    });
  }, [sortedTiers]);

  useEffect(() => {
    const nextEditorKey = editState
      ? `${editState.mode}:${editState.editSortOrder ?? 'new'}`
      : null;

    if (!nextEditorKey) {
      previousEditorKeyRef.current = null;
      return;
    }

    if (nextEditorKey === previousEditorKeyRef.current) {
      return;
    }

    previousEditorKeyRef.current = nextEditorKey;

    const frame = requestAnimationFrame(() => {
      nameInputRef.current?.focus({ preventScroll: true });
    });

    return () => cancelAnimationFrame(frame);
  }, [editState?.mode, editState?.editSortOrder]);

  useEffect(() => {
    if (editState?.mode === 'add') {
      setStagedAddState(editState);
    }
  }, [editState]);

  const handleEnabledChange = useCallback((newEnabled: boolean) => {
    onEnabledChange(newEnabled);
    if (newEnabled && tiers.length === 0 && defaultTiers && defaultTiers.length > 0) {
      onTiersChange(defaultTiers.map(t => ({ ...t })));
    }
    if (!newEnabled) {
      setEditState(null);
    }
  }, [onEnabledChange, tiers.length, defaultTiers, onTiersChange]);

  // Lock the add-tier region's min-height during crossfade to prevent height valley.
  // After the crossfade completes, animate min-height down to 0 to avoid a snap.
  const lockAddRegionHeight = useCallback(() => {
    const region = addRegionRef.current;
    if (!region) return;
    if (addRegionLockTimerRef.current) clearTimeout(addRegionLockTimerRef.current);
    // Freeze at current height without transition
    region.style.transition = 'none';
    region.style.minHeight = `${region.getBoundingClientRect().height}px`;

    const dur = shouldReduceMotion ? 0 : collapseDuration;
    addRegionLockTimerRef.current = setTimeout(() => {
      // Animate min-height to 0 so the release doesn't snap
      region.style.transition = `min-height ${dur}ms cubic-bezier(0.4, 0, 0.2, 1)`;
      region.style.minHeight = '0px';
      addRegionLockTimerRef.current = setTimeout(() => {
        region.style.transition = '';
        region.style.minHeight = '';
        addRegionLockTimerRef.current = null;
      }, dur + 50);
    }, dur + 80);
  }, [collapseDuration, shouldReduceMotion]);

  const handleAdd = useCallback(() => {
    isDraggingRef.current = false;
    setSaveAttempted(false);
    // Capture the add-button area height before it collapses — used as
    // the min-height floor during save so the crossfade never dips below it.
    if (addRegionRef.current) {
      addButtonHeightRef.current = addRegionRef.current.getBoundingClientRect().height;
    }
    lockAddRegionHeight();
    setEditState(EMPTY_ADD_STATE);
  }, [lockAddRegionHeight]);

  const getDisplayOrder = useCallback(
    () => (isDraggingRef.current ? localOrder : sortedTiers),
    [localOrder, sortedTiers],
  );

  const handleEdit = useCallback((index: number) => {
    const tier = getDisplayOrder()[index];
    setSaveAttempted(false);
    setEditState({ mode: 'edit', name: tier.name, rate: tier.rate, editIndex: index, editSortOrder: tier.sortOrder });
  }, [getDisplayOrder]);

  const handleCancel = useCallback(() => {
    suppressMobileTapUntilRef.current = Date.now() + 400;
    isDraggingRef.current = false;
    setSaveAttempted(false);
    lockAddRegionHeight();
    setEditState(null);
  }, [lockAddRegionHeight]);

  const handleSave = useCallback(() => {
    if (!editState) return;
    const { name, rate, mode, editIndex, editSortOrder } = editState;
    const displayOrder = getDisplayOrder();
    const resolvedEditIndex = mode === 'edit'
      ? (editSortOrder != null
        ? displayOrder.findIndex((tier) => tier.sortOrder === editSortOrder)
        : (editIndex ?? -1))
      : null;

    const validationError = validateExtendedTierCandidate({
      tiers: displayOrder,
      highestOvertimeRate,
      name,
      rate,
      mode,
      editIndex: resolvedEditIndex,
    });
    if (validationError) {
      setSaveAttempted(true);
      toast.error(validationError);
      return;
    }
    if (rate == null) return;

    const savedRate = rate;

    if (mode === 'add') {
      const maxSort = displayOrder.length > 0
        ? Math.max(...displayOrder.map(t => t.sortOrder))
        : 0;
      isDraggingRef.current = false;
      // Smooth the height transition from form height → button height.
      // Lock at current height, then immediately animate min-height to 0.
      // The content's natural height (button @ ~48px) catches the descent,
      // producing a single monotonic decline instead of a dip or two-phase drop.
      if (addRegionRef.current) {
        const region = addRegionRef.current;
        const dur = shouldReduceMotion ? 0 : collapseDuration;
        if (addRegionLockTimerRef.current) clearTimeout(addRegionLockTimerRef.current);
        region.style.transition = 'none';
        region.style.minHeight = `${region.getBoundingClientRect().height}px`;
        // Force style flush then start transition
        region.offsetHeight; // eslint-disable-line @typescript-eslint/no-unused-expressions
        region.style.transition = `min-height ${dur + 100}ms cubic-bezier(0.4, 0, 0.2, 1)`;
        // Transition to the add button's intrinsic min-height (min-h-11 = 44px)
        // rather than 0 — this covers the crossfade dip without causing a snap
        // on release since the final content is always ≥ 44px.
        region.style.minHeight = '44px';
        addRegionLockTimerRef.current = setTimeout(() => {
          region.style.transition = '';
          region.style.minHeight = '';
          addRegionLockTimerRef.current = null;
        }, dur + 200);
      }
      onTiersChange(normalizeExtendedTiers([...tiers, { name: name.trim(), rate: savedRate, sortOrder: maxSort + 1 }], highestOvertimeRate));
      toast.success(`Added ${name.trim()} (${savedRate}x)`);
    } else if (resolvedEditIndex != null && resolvedEditIndex >= 0) {
      const target = displayOrder[resolvedEditIndex];
      const updated = tiers.map((t) => {
        if (t.sortOrder === target.sortOrder) {
          return { ...t, name: name.trim(), rate: savedRate };
        }
        return t;
      });
      onTiersChange(normalizeExtendedTiers(updated, highestOvertimeRate));
    } else {
      toast.error('Unable to save tier');
      return;
    }
    suppressMobileTapUntilRef.current = Date.now() + 400;
    setEditState(null);
  }, [editState, highestOvertimeRate, getDisplayOrder, tiers, onTiersChange, lockAddRegionHeight]);

  const shouldAllowMobileTap = useCallback(() => Date.now() >= suppressMobileTapUntilRef.current, []);

  const handleDelete = useCallback((index: number) => {
    const tier = getDisplayOrder()[index];
    const remaining = tiers.filter(t => t.sortOrder !== tier.sortOrder);
    const resequenced = normalizeExtendedTiers(remaining, highestOvertimeRate);
    suppressMobileTapUntilRef.current = Date.now() + 400;
    isDraggingRef.current = false;
    onTiersChange(resequenced);
    setEditState(null);
    toast.success(`Deleted ${tier.name}`);
  }, [getDisplayOrder, highestOvertimeRate, tiers, onTiersChange]);

  const handleDragStart = useCallback(() => {
    isDraggingRef.current = true;
  }, []);

  // During drag — update local order only (same references, no new objects)
  const handleReorder = useCallback((reordered: ExtendedTier[]) => {
    if (!isDraggingRef.current) return;
    const currentSorted = sortedTiersRef.current;
    if (reordered.length !== currentSorted.length) return;
    const toKey = (tier: ExtendedTier) => `${tier.sortOrder}:${tier.name}:${tier.rate ?? ''}`;
    const currentKeys = new Set(currentSorted.map(toKey));
    if (!reordered.every((tier) => currentKeys.has(toKey(tier)))) return;
    setLocalOrder(reordered);
  }, []);

  // On drag end — persist the new sort order to parent
  const handleDragEnd = useCallback(() => {
    isDraggingRef.current = false;
    const currentOrder = localOrder;
    const updated = currentOrder.map((t, i) => ({ ...t, sortOrder: i + 1 }));

    if (editState?.mode === 'edit' && editState.editSortOrder != null) {
      const editedIndex = currentOrder.findIndex((tier) => tier.sortOrder === editState.editSortOrder);
      if (editedIndex >= 0) {
        setEditState(prev => prev && prev.mode === 'edit'
          ? { ...prev, editIndex: editedIndex, editSortOrder: editedIndex + 1 }
          : prev
        );
      }
    }

    onTiersChange(normalizeExtendedTiers(updated, highestOvertimeRate));
  }, [localOrder, editState, highestOvertimeRate, onTiersChange]);

  // Keyboard-driven reorder (arrow keys on drag handle)
  const handleMoveUp = useCallback((index: number) => {
    if (index <= 0) return;
    const order = getDisplayOrder();
    const updated = [...order];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];

    if (editState?.mode === 'edit' && editState.editSortOrder != null) {
      const editedIndex = updated.findIndex((tier) => tier.sortOrder === editState.editSortOrder);
      if (editedIndex >= 0) {
        setEditState(prev => prev && prev.mode === 'edit'
          ? { ...prev, editIndex: editedIndex, editSortOrder: editedIndex + 1 }
          : prev
        );
      }
    }

    isDraggingRef.current = false;
    onTiersChange(normalizeExtendedTiers(updated.map((t, i) => ({ ...t, sortOrder: i + 1 })), highestOvertimeRate));
  }, [getDisplayOrder, editState, highestOvertimeRate, onTiersChange]);

  const handleMoveDown = useCallback((index: number) => {
    const order = getDisplayOrder();
    if (index >= order.length - 1) return;
    const updated = [...order];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];

    if (editState?.mode === 'edit' && editState.editSortOrder != null) {
      const editedIndex = updated.findIndex((tier) => tier.sortOrder === editState.editSortOrder);
      if (editedIndex >= 0) {
        setEditState(prev => prev && prev.mode === 'edit'
          ? { ...prev, editIndex: editedIndex, editSortOrder: editedIndex + 1 }
          : prev
        );
      }
    }

    isDraggingRef.current = false;
    onTiersChange(normalizeExtendedTiers(updated.map((t, i) => ({ ...t, sortOrder: i + 1 })), highestOvertimeRate));
  }, [getDisplayOrder, editState, highestOvertimeRate, onTiersChange]);

  const presetSuggestions = useMemo(() => {
    const usedNames = new Set(sortedTiers.map(t => t.name.toLowerCase()));
    return MULTIPLIER_SUGGESTIONS.filter(
      s => !usedNames.has(s.name.toLowerCase()) && s.rate > highestOvertimeRate
    );
  }, [sortedTiers, highestOvertimeRate]);

  const xActsAsDelete = Boolean(
    editState
      && isMobileViewport
      && editState.mode === 'edit'
      && editState.editIndex != null
  );

  const renderInlineFormCard = useCallback((formState: EditState, options?: { isActive?: boolean; xActsAsDelete?: boolean }) => {
    const isActive = options?.isActive ?? false;
    const shouldDeleteOnDismiss = options?.xActsAsDelete ?? false;

    return (
      <div
        data-anim-audit="short-turn-extended-overtime-inline-form"
        className={cn(
          'border border-primary/40 bg-surface-1 dark:bg-transparent rounded equipment-card [--bevel-color:color-mix(in_srgb,var(--primary),transparent_75%)]',
          'overflow-hidden'
        )}
      >
        <div className="grid grid-cols-[auto_56px_1fr_auto_auto] items-center gap-2 px-3 py-2">
          <div className="-ml-3 -my-2 flex items-center justify-center w-11 h-11 text-muted-foreground/20">
            <GripVertical className="h-4 w-4" />
          </div>
          <div
            className={cn(
              'relative inline-flex justify-self-start items-baseline whitespace-nowrap font-mono text-sm font-bold tabular-nums cursor-text',
              formState.rate == null
                ? 'text-muted-foreground/50'
                : 'text-primary-text'
            )}
            onClick={(e) => {
              const input = (e.currentTarget as HTMLElement).querySelector('input');
              if (input && e.target !== input) input.focus();
            }}
          >
            {formState.rate == null ? (
              <span className="pointer-events-none absolute left-0 top-1/2 -translate-x-full -translate-y-1/2 leading-none">&gt;</span>
            ) : null}
            <input
              data-anim-audit="short-turn-extended-overtime-rate-input"
              type="number"
              step="0.1"
              min={highestOvertimeRate + 0.1}
              max={MAX_EXTENDED_TIER_RATE}
              aria-label={isActive ? 'Multiplier rate' : undefined}
              aria-invalid={isActive && saveAttempted && (formState.rate === null || formState.rate <= highestOvertimeRate) || undefined}
              value={formState.rate ?? ''}
              onChange={(e) => setEditState(prev => {
                if (!prev) return prev;
                const rawRate = e.target.value;
                if (!rawRate) return { ...prev, rate: null };
                const parsedRate = parseFloat(rawRate);
                if (!Number.isFinite(parsedRate)) return { ...prev, rate: null };
                if (parsedRate > MAX_EXTENDED_TIER_RATE) return prev;
                return { ...prev, rate: parsedRate };
              })}
              className="min-w-[1ch] text-left leading-none !h-auto !rounded-none !border-0 !bg-transparent !p-0 !shadow-none !ring-0 outline-none focus:!ring-0 focus:!outline-none focus-visible:!ring-0 focus-visible:!outline-none placeholder:text-muted-foreground/50 placeholder:opacity-100 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                width: `${Math.max(String(formState.rate ?? highestOvertimeRate).length, 1)}ch`,
                font: 'inherit',
                color: 'inherit',
                WebkitTextFillColor: 'currentColor',
              }}
              placeholder={String(highestOvertimeRate)}
            />
            <span className="pointer-events-none leading-none">x</span>
          </div>
          <div className="w-full font-heading text-[13px] uppercase tracking-wide text-foreground">
            <input
              ref={isActive ? nameInputRef : undefined}
              data-anim-audit="short-turn-extended-overtime-name-input"
              type="text"
              aria-label={isActive ? 'Tier name' : undefined}
              aria-invalid={isActive && saveAttempted && !formState.name.trim() || undefined}
              value={formState.name}
              onChange={(e) => setEditState(prev => prev ? { ...prev, name: e.target.value } : prev)}
              className="w-full shadow-none focus:outline-none focus-visible:shadow-[0_1px_0_0_var(--primary)] placeholder:normal-case placeholder:tracking-normal placeholder:text-muted-foreground/50"
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                font: 'inherit',
                color: 'inherit',
                WebkitTextFillColor: 'currentColor',
                letterSpacing: 'inherit',
                textTransform: 'inherit',
              }}
              placeholder="EXTENDED TIER"
            />
          </div>
          <span className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] sm:text-[12px] font-mono font-semibold leading-none bg-primary/15 text-primary-text">
            Ext {formState.mode === 'add' ? sortedTiers.length + 1 : (formState.editIndex != null ? formState.editIndex + 1 : '')}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-anim-audit="short-turn-extended-overtime-cancel"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (shouldDeleteOnDismiss && formState.mode === 'edit' && formState.editIndex != null) {
                  handleDelete(formState.editIndex);
                  return;
                }
                handleCancel();
              }}
              className={cn(
                'relative w-7 h-7 !min-h-0 flex items-center justify-center rounded transition-colors',
                "after:content-[''] after:absolute after:-inset-2",
                shouldDeleteOnDismiss
                  ? 'text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10'
                  : 'text-muted-foreground/60 hover:text-foreground hover:bg-muted/30'
              )}
              aria-label={shouldDeleteOnDismiss ? 'Delete tier' : 'Cancel'}
            >
              {shouldDeleteOnDismiss ? <Trash2 className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
            </button>
            <button
              type="button"
              data-anim-audit="short-turn-extended-overtime-save"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => { e.stopPropagation(); handleSave(); }}
              className="relative w-7 h-7 !min-h-0 flex items-center justify-center rounded text-primary-text hover:text-primary-foreground hover:bg-primary/90 transition-colors after:content-[''] after:absolute after:-inset-2"
              aria-label="Save"
            >
              <Check className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  }, [handleCancel, handleDelete, handleSave, highestOvertimeRate, saveAttempted, sortedTiers.length]);

  // Expose configure action to parent via ref — enable + open add-tier mode + scroll into view.
  const handleConfigure = useCallback(() => {
    isDraggingRef.current = false;
    setSaveAttempted(false);

    if (!enabled) {
      handleEnabledChange(true);
    }

    setEditState(EMPTY_ADD_STATE);
    requestAnimationFrame(() => {
      inlineSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }, [enabled, handleEnabledChange]);

  useEffect(() => {
    if (configureRef) configureRef.current = handleConfigure;
    return () => { if (configureRef) configureRef.current = null; };
  }, [configureRef, handleConfigure]);

  const inlineFormCard = editState
    ? renderInlineFormCard(editState, { isActive: true, xActsAsDelete })
    : null;

  // --- Tier list content rendered inline ---
  const listRef = useRef<HTMLDivElement>(null);
  // --- Ghost card: shows the top of the base rate ladder for context ---
  const ghostCard = highestOvertimeTierName ? (
    <div
      aria-hidden="true"
      className={cn(
        'group grid grid-cols-[auto_56px_1fr_auto_auto] items-center gap-2 px-3 py-2',
        'border border-dashed border-border/40 bg-transparent rounded equipment-card [--bevel-color:rgba(255,255,255,0.08)] opacity-45',
        'pointer-events-none select-none'
      )}
    >
      {/* Drag handle slot — same box model as tier cards to keep columns aligned */}
      <div className="-ml-3 -my-2 flex items-center justify-center w-11 h-11 text-muted-foreground/30">
        <GripVertical className="h-4 w-4" />
      </div>
      <div className="font-mono text-sm font-bold tabular-nums text-muted-foreground">
        {highestOvertimeRate}x
      </div>
      <div className="font-heading text-[13px] uppercase tracking-wide truncate text-muted-foreground">
        {highestOvertimeTierName}
      </div>
      <span className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] sm:text-[12px] font-mono font-semibold leading-none bg-muted/50 text-muted-foreground">
        Ladder Top
      </span>
      <div className="hidden md:flex items-center gap-1 overflow-hidden md:max-w-[60px] lg:max-w-0 transition-[max-width] duration-200 ease-in-out" aria-hidden="true">
        <span className="w-7 h-7 shrink-0" />
        <span className="w-7 h-7 shrink-0" />
      </div>
    </div>
  ) : null;

  const canUseLocalDragOrder = useMemo(() => {
    if (!isDraggingRef.current) return false;
    if (localOrder.length !== sortedTiers.length) return false;
    const toKey = (tier: ExtendedTier) => `${tier.sortOrder}:${tier.name}:${tier.rate ?? ''}`;
    const sortedKeys = new Set(sortedTiers.map(toKey));
    return localOrder.every((tier) => sortedKeys.has(toKey(tier)));
  }, [localOrder, sortedTiers]);

  // Defensive fallback: if drag state is stale, always prefer canonical tier order from props.
  const displayedOrder = canUseLocalDragOrder ? localOrder : sortedTiers;

  useEffect(() => {
    if (!canUseLocalDragOrder && isDraggingRef.current) {
      isDraggingRef.current = false;
    }
  }, [canUseLocalDragOrder]);

  const addFormState = editState?.mode === 'add'
    ? editState
    : (stagedAddState ?? EMPTY_ADD_STATE);
  const shouldRenderStagedAddForm = editState?.mode === 'add' || stagedAddState !== null;

  const tierListContent = (
    <motion.div
      ref={listRef}
      layout={shouldReduceMotion ? undefined : "position"}
      className="flex flex-col gap-2"
      data-anim-audit="short-turn-extended-overtime-tier-list"
    >
      {ghostCard}
      <Reorder.Group
        axis="y"
        values={displayedOrder}
        onReorder={handleReorder}
        className="flex flex-col gap-2"
        layoutScroll
      >
        <AnimatePresence initial={false}>
          {displayedOrder.map((tier, idx) => {
            const isEditing = editState?.mode === 'edit' && editState.editIndex === idx;
            return (
              <DraggableTierCard
                key={`${tier.sortOrder}:${tier.name}`}
                tier={tier}
                idx={idx}
                isEditing={isEditing}
                editForm={inlineFormCard}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onMoveUp={handleMoveUp}
                onMoveDown={handleMoveDown}
                constraintsRef={listRef}
                disabled={editState !== null}
                dragDisabled={false}
                unreachable={maxTiers != null && idx >= maxTiers}
                totalCount={displayedOrder.length}
                isMobile={!!isMobileViewport}
                reducedMotion={!!shouldReduceMotion}
                shouldAllowMobileTap={shouldAllowMobileTap}
              />
            );
          })}
        </AnimatePresence>
      </Reorder.Group>

      {/* Unreachable tiers explanation */}
      {maxTiers != null && sortedTiers.length > maxTiers && editState === null && (
        <p className="text-[11px] sm:text-[12px] font-mono text-muted-foreground/70 text-center px-2 pt-1">
          Your overtime ladder has {maxTiers} {maxTiers === 1 ? 'threshold' : 'thresholds'} — only {maxTiers === 1 ? 'the first tier is' : `the first ${maxTiers} tiers are`} reachable. Remove or add overtime tiers to your ladder to adjust.
        </p>
      )}

      <div
        ref={addRegionRef}
        className="grid"
        style={{ gridTemplateAreas: '"swap"' }}
        data-anim-audit="short-turn-extended-overtime-add-tier-region"
      >
        {shouldRenderStagedAddForm ? (
          <AnimatedCollapse open={editState?.mode === 'add'} duration={collapseDuration} className="[grid-area:swap]">
            <motion.div
              layout={shouldReduceMotion ? undefined : "position"}
              transition={{ duration: shouldReduceMotion ? 0 : DURATION_FAST, ease: EASE_EXPO_OUT }}
              // Hide content during close animation so the form doesn't flash
              // as a duplicate of the newly-saved tier card.
              style={editState?.mode !== 'add' ? { visibility: 'hidden' } : undefined}
            >
              <div className="flex flex-col gap-2">
                {renderInlineFormCard(addFormState, { isActive: editState?.mode === 'add' })}
                {presetSuggestions.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {presetSuggestions.map((s) => (
                      <button
                        key={s.name}
                        type="button"
                        onClick={() => setEditState(prev => prev ? { ...prev, name: s.name, rate: s.rate } : prev)}
                        className={cn(
                          'relative inline-flex items-center whitespace-nowrap text-[11px] sm:text-[12px] px-2 py-1 rounded border border-dashed',
                          "after:content-[''] after:absolute after:-inset-2",
                          'border-primary/30 bg-primary/5 text-primary-text/70',
                          'hover:text-primary-text hover:border-primary/60 hover:bg-primary/10 cursor-pointer',
                          'transition-colors duration-100'
                        )}
                      >
                        {s.name} ({s.rate}x)
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </AnimatedCollapse>
        ) : null}

        <AnimatedCollapse
          open={editState?.mode !== 'edit' && editState?.mode !== 'add'}
          duration={collapseDuration}
          className="[grid-area:swap]"
        >
          <motion.div
            layout={shouldReduceMotion ? undefined : "position"}
            transition={{ duration: shouldReduceMotion ? 0 : DURATION_FAST, ease: EASE_EXPO_OUT }}
            className={cn(
              'flex flex-col items-center gap-2',
              sortedTiers.length === 0 ? 'pt-0' : 'pt-1'
            )}
          >
            {maxTiers != null && sortedTiers.length >= maxTiers ? (
              <p className="text-[11px] sm:text-[12px] font-mono text-muted-foreground/70 text-center">
                Your overtime ladder has {maxTiers} {maxTiers === 1 ? 'threshold' : 'thresholds'} — at most {maxTiers} extended {maxTiers === 1 ? 'tier' : 'tiers'} can be reached.
              </p>
            ) : (
              <button
                type="button"
                onPointerDown={(e) => e.preventDefault()}
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleAdd}
                data-anim-audit="short-turn-extended-overtime-add-tier"
                className={cn(
                  'inline-flex items-center gap-1 min-h-11 text-xs font-mono transition-colors cursor-pointer rounded px-4 py-2 active:translate-y-px',
                  sortedTiers.length === 0
                    ? 'text-primary-text border border-primary/40 hover:border-primary/60 animate-pulse-amber'
                    : 'text-primary-text/60 hover:text-primary-text border border-border/50 hover:border-primary/40'
                )}
              >
                <Plus className="h-3 w-3" />
                Extended Tier
              </button>
            )}
            <AnimatePresence>
              {sortedTiers.length === 0 && (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: shouldReduceMotion ? 0 : DURATION_MEDIUM, ease: EASE_EXPO_OUT }}
                  className="text-xs font-mono text-muted-foreground italic overflow-hidden"
                >
                  <span className="text-primary-text/70">tip:</span> The order of your tiers matters — drag to rearrange.
                </motion.p>
              )}
            </AnimatePresence>
          </motion.div>
        </AnimatedCollapse>
      </div>
    </motion.div>
  );

  const inlineEditorSection = mode === 'editor-only' ? (
    <div
      ref={inlineSectionRef}
      className="flex flex-col gap-2"
      data-anim-audit="short-turn-extended-overtime-inline-section"
    >
      {tierListContent}
    </div>
  ) : (
    <div
      ref={inlineSectionRef}
      className="flex flex-col gap-2"
      data-anim-audit="short-turn-extended-overtime-inline-section"
    >
      <div
        className={cn(
          'rounded border border-border/60 bg-surface-1/30 dark:bg-transparent',
          'equipment-card [--bevel-color:rgba(255,255,255,0.10)]',
          'p-2 sm:p-2'
        )}
        data-anim-audit="short-turn-extended-overtime-inline-shell"
      >
        {tierListContent}
      </div>
    </div>
  );

  if (mode === 'editor-only') {
    return (
      <div
        className="space-y-0"
        data-anim-audit="short-turn-extended-overtime-field"
        data-anim-audit-open="true"
        data-anim-audit-tier-count={sortedTiers.length}
      >
        {inlineEditorSection}
      </div>
    );
  }

  return (
    <div
      className={cn('space-y-3', !enabled && 'space-y-0')}
      data-anim-audit="short-turn-extended-overtime-field"
      data-anim-audit-open={enabled ? 'true' : 'false'}
      data-anim-audit-tier-count={sortedTiers.length}
    >
      {/* Toggle row */}
      <div className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center" data-anim-audit="short-turn-extended-overtime-toggle-row">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <label htmlFor={toggleId} className={labelClassName}>Extended Overtime</label>
            <CustomizedFieldIndicator isCustomized={isCustomized} context={context} />
            {helpContent && (
              <span data-anim-audit="short-turn-extended-overtime-help-button-slot">
                <HelpButton
                  fieldName={helpFieldNamePrefix ? `${helpFieldNamePrefix} - Extended Overtime` : 'Extended Overtime'}
                  content={helpContent}
                  variables={helpVariables}
                  injectableComponents={helpInjectableComponents}
                  enableFieldFocus
                />
              </span>
            )}
          </div>
          <p className={cn(descriptionClassName, 'hidden md:block md:max-w-[44ch]')}>
            Allow surcharge to extend above your highest overtime tier with an optional surcharge ladder.
          </p>
        </div>
        <div className="flex justify-end items-center">
          <Switch
            id={toggleId}
            checked={enabled}
            onCheckedChange={handleEnabledChange}
            data-anim-audit="short-turn-extended-overtime-field-toggle"
          />
        </div>
        <p className={cn(descriptionClassName, 'col-span-2 md:hidden')}>
          Allow surcharge to extend above your highest overtime tier with an optional surcharge ladder.
        </p>
      </div>

      {/* Inline tier management */}
      <AnimatedCollapse open={enabled}>
        {inlineEditorSection}
      </AnimatedCollapse>
    </div>
  );
};
